/**
 * Hook for response path typeahead in API sequence inputs.
 *
 * Parses a JSON response body and extracts all dot-notation paths
 * as autocomplete suggestions. Provides keyboard navigation and
 * filtering based on current input.
 *
 * @module FlowDesigner/api/hooks/useResponsePathTypeahead
 */

import { useState, useCallback, useRef, useMemo, useEffect } from 'react';

export interface PathSuggestion {
  path: string;
  value: unknown;
  preview: string;
}

export interface UseResponsePathTypeaheadResult {
  isOpen: boolean;
  suggestions: PathSuggestion[];
  highlightedIndex: number;
  onKeyDown: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onFocus: () => void;
  onBlur: () => void;
  selectSuggestion: (suggestion: PathSuggestion) => void;
  dismiss: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
  hasSuggestions: boolean;
}

const MAX_DEPTH = 5;
const MAX_SUGGESTIONS = 50;

/** Format a value for display preview, truncating if necessary. */
function formatPreview(value: unknown): string {
  if (value === undefined) return 'undefined';
  if (value === null) return 'null';
  if (typeof value === 'string') {
    return value.length > 40 ? `"${value.slice(0, 37)}..."` : `"${value}"`;
  }
  if (typeof value === 'object') {
    const json = JSON.stringify(value);
    return json.length > 40 ? `${json.slice(0, 37)}...` : json;
  }
  return String(value);
}

/** Recursively extract all dot-notation paths from a JSON object. */
function extractJsonPaths(
  obj: unknown,
  prefix: string = '',
  depth: number = 0,
  results: PathSuggestion[] = []
): PathSuggestion[] {
  if (depth > MAX_DEPTH || results.length >= MAX_SUGGESTIONS) return results;

  if (obj === null || obj === undefined || typeof obj !== 'object') {
    return results;
  }

  if (Array.isArray(obj)) {
    if (obj.length > 0) {
      const arrayPath = prefix ? `${prefix}[0]` : '[0]';
      const firstItem = obj[0];

      if (firstItem !== null && typeof firstItem === 'object') {
        extractJsonPaths(firstItem, arrayPath, depth + 1, results);
      } else if (results.length < MAX_SUGGESTIONS) {
        results.push({ path: arrayPath, value: firstItem, preview: formatPreview(firstItem) });
      }
    }
    return results;
  }

  for (const key of Object.keys(obj)) {
    if (results.length >= MAX_SUGGESTIONS) break;

    const fullPath = prefix ? `${prefix}.${key}` : key;
    const value = (obj as Record<string, unknown>)[key];

    if (value !== null && typeof value === 'object') {
      results.push({ path: fullPath, value, preview: formatPreview(value) });
      extractJsonPaths(value, fullPath, depth + 1, results);
    } else {
      results.push({ path: fullPath, value, preview: formatPreview(value) });
    }
  }

  return results;
}

export function useResponsePathTypeahead(
  jsonSource: string | null | undefined,
  inputValue: string,
  onSelectPath: (path: string) => void
): UseResponsePathTypeaheadResult {
  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  // Track whether the input is focused to handle blur properly
  const blurTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Parse JSON and extract all paths
  const allPaths = useMemo((): PathSuggestion[] => {
    if (!jsonSource) return [];
    try {
      const parsed = JSON.parse(jsonSource);
      return extractJsonPaths(parsed);
    } catch {
      return [];
    }
  }, [jsonSource]);

  // Filter suggestions based on current input
  const suggestions = useMemo(() => {
    if (!isOpen || allPaths.length === 0) return [];
    if (!inputValue) return allPaths;

    const lower = inputValue.toLowerCase();
    return allPaths.filter((s) => s.path.toLowerCase().includes(lower));
  }, [isOpen, allPaths, inputValue]);

  // Reset highlight when suggestions change
  useEffect(() => {
    setHighlightedIndex(0);
  }, [suggestions.length, inputValue]);

  const dismiss = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(0);
  }, []);

  const onFocus = useCallback(() => {
    if (blurTimeoutRef.current) {
      clearTimeout(blurTimeoutRef.current);
      blurTimeoutRef.current = null;
    }
    if (allPaths.length > 0) {
      setIsOpen(true);
    }
  }, [allPaths.length]);

  const onBlur = useCallback(() => {
    // Delay to allow click on suggestion to fire first
    blurTimeoutRef.current = setTimeout(() => {
      setIsOpen(false);
    }, 150);
  }, []);

  const selectSuggestion = useCallback(
    (suggestion: PathSuggestion) => {
      if (blurTimeoutRef.current) {
        clearTimeout(blurTimeoutRef.current);
        blurTimeoutRef.current = null;
      }
      onSelectPath(suggestion.path);
      dismiss();
    },
    [onSelectPath, dismiss]
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (!isOpen || suggestions.length === 0) {
        if (e.key === 'ArrowDown' && allPaths.length > 0) {
          e.preventDefault();
          setIsOpen(true);
        }
        return;
      }

      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.min(prev + 1, suggestions.length - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setHighlightedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        if (suggestions.length > 0) {
          e.preventDefault();
          selectSuggestion(suggestions[highlightedIndex]!);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        dismiss();
      }
    },
    [isOpen, suggestions, highlightedIndex, selectSuggestion, dismiss, allPaths.length]
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (blurTimeoutRef.current) clearTimeout(blurTimeoutRef.current);
    };
  }, []);

  return {
    isOpen,
    suggestions,
    highlightedIndex,
    onKeyDown,
    onFocus,
    onBlur,
    selectSuggestion,
    dismiss,
    inputRef,
    hasSuggestions: allPaths.length > 0,
  };
}
